import { Button } from "@fp/ui/components/base/buttons/button";
import {
	PageContentContainer,
	PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { AdminLayout } from "@/components/admin-layout";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState /*useEffect, useRef*/ } from "react";
import axios from "@/lib/axios";
import { useUserContext } from "@/components/user-context";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { Check, AlertCircle, Clock } from "@untitledui/icons";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";

interface InvitationDetailsDto {
	id: string;
	organizationId: string;
	organizationName: string;
	email: string;
	role: string;
	invitationType: string;
	expiryDate: string;
	isExpired: boolean;
	isAccepted: boolean;
	isActive: boolean;
	invitedByUserFullName: string | null;
}

export const Route = createFileRoute("/invitation/$token")({
	component: RouteComponent,
	pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
	const { token } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { claims, refetchOrganizations } = useUserContext();
	const [error, setError] = useState<string | null>(null);
	const [showDeclineModal, setShowDeclineModal] = useState(false);

	const {
		data: invitation,
		isLoading,
		error: fetchError,
	} = useQuery({
		queryKey: ["invitation", token],
		queryFn: async () => {
			const response = await axios.get<InvitationDetailsDto>(
				`/api/received-invitations/by-token/${token}`,
			);
			return response.data;
		},
	});

	const acceptMutation = useMutation({
		mutationFn: async () => {
			await axios.post(`/api/received-invitations/by-token/${token}/accept`);
		},
		onSuccess: async () => {
			await refetchOrganizations();
			queryClient.invalidateQueries({ queryKey: ["invitation", token] });
			navigate({ to: "/dashboard" });
		},
		onError: (err: any) => {
			setError(
				err.response?.data?.message ||
					"Failed to accept invitation. Please try again.",
			);
		},
	});

	const declineMutation = useMutation({
		mutationFn: async (id: string) => {
			await axios.post(`/api/received-invitations/${id}/decline`);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["invitation", token] });
			navigate({ to: "/dashboard" });
		},
		onError: (err: any) => {
			setError(
				err.response?.data?.message ||
					"Failed to decline invitation. Please try again.",
			);
		},
	});

	// TODO: Uncomment to enable auto-accept behavior
	// Auto-accept the invitation when the page loads if:
	// - Invitation is valid (not expired, not cancelled, not already accepted)
	// - User's email matches the invitation email
	// const hasAutoAccepted = useRef(false);
	// useEffect(() => {
	//   if (
	//     invitation &&
	//     !hasAutoAccepted.current &&
	//     !invitation.isExpired &&
	//     invitation.isActive &&
	//     !invitation.isAccepted &&
	//     claims?.name &&
	//     claims.name.toLowerCase() === invitation.email.toLowerCase()
	//   ) {
	//     hasAutoAccepted.current = true;
	//     acceptMutation.mutate();
	//   }
	// }, [invitation, claims, acceptMutation]);

	const handleAccept = () => {
		setError(null);
		acceptMutation.mutate();
	};

	const handleDecline = () => {
		setShowDeclineModal(true);
	};

	const handleDeclineConfirm = () => {
		if (invitation) {
			setError(null);
			declineMutation.mutate(invitation.id);
			setShowDeclineModal(false);
		}
	};

	if (isLoading) {
		return (
			<AdminLayout>
				<PageContentContainer className="flex items-center justify-center min-h-screen">
					<PageLoading />
				</PageContentContainer>
			</AdminLayout>
		);
	}

	if (fetchError || !invitation) {
		return (
			<AdminLayout>
				<PageContentContainer className="flex items-center justify-center min-h-screen">
					<div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-8 text-center">
						<div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
							<AlertCircle className="w-6 h-6 text-red-600" />
						</div>
						<h1 className="text-xl font-semibold text-gray-900 mb-2">
							Invalid Invitation
						</h1>
						<p className="text-gray-600 mb-6">
							This invitation link is invalid or has already been used.
						</p>
						<Button
							color="primary"
							onClick={() => navigate({ to: "/dashboard" })}
						>
							Go to Dashboard
						</Button>
					</div>
				</PageContentContainer>
			</AdminLayout>
		);
	}

	const isExpiredOrCancelled = invitation.isExpired || !invitation.isActive;
	const isAlreadyAccepted = invitation.isAccepted;
	const emailMismatch =
		claims?.name &&
		claims.name.toLowerCase() !== invitation.email.toLowerCase();

	return (
		<AdminLayout>
			<PageContentContainer className="flex items-center justify-center min-h-screen">
				<div className="max-w-md w-full bg-white border border-gray-200 rounded-lg p-8">
					{isAlreadyAccepted ? (
						<>
							<div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<Check className="w-6 h-6 text-green-600" />
							</div>
							<h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
								Already Accepted
							</h1>
							<p className="text-gray-600 text-center mb-6">
								This invitation has already been accepted.
							</p>
							<Button
								color="primary"
								onClick={() => navigate({ to: "/dashboard" })}
								className="w-full"
							>
								Go to Dashboard
							</Button>
						</>
					) : isExpiredOrCancelled ? (
						<>
							<div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<Clock className="w-6 h-6 text-yellow-600" />
							</div>
							<h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
								Invitation {invitation.isExpired ? "Expired" : "Cancelled"}
							</h1>
							<p className="text-gray-600 text-center mb-6">
								This invitation is no longer valid. Please contact the
								organization administrator for a new invitation.
							</p>
							<Button
								color="secondary"
								onClick={() => navigate({ to: "/dashboard" })}
								className="w-full"
							>
								Go to Dashboard
							</Button>
						</>
					) : emailMismatch ? (
						<>
							<div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
								<AlertCircle className="w-6 h-6 text-yellow-600" />
							</div>
							<h1 className="text-xl font-semibold text-gray-900 text-center mb-2">
								Email Mismatch
							</h1>
							<p className="text-gray-600 text-center mb-4">
								This invitation was sent to{" "}
								<span className="font-medium">{invitation.email}</span>, but
								you're signed in as{" "}
								<span className="font-medium">{claims?.name}</span>.
							</p>
							<p className="text-sm text-gray-500 text-center mb-6">
								Please sign out and sign in with the correct account to accept
								this invitation.
							</p>
							<Button
								color="secondary"
								onClick={() => navigate({ to: "/profile" })}
								className="w-full"
							>
								Go to Profile
							</Button>
						</>
					) : (
						<>
							<h1 className="text-xl font-semibold text-gray-900 text-center mb-4">
								You've Been Invited
							</h1>

							<p className="text-gray-600 text-center mb-6">
								{invitation.invitedByUserFullName ? (
									<>
										<span className="font-semibold">
											{invitation.invitedByUserFullName}
										</span>{" "}
										has invited you to join{" "}
										<span className="font-semibold">
											{invitation.organizationName}
										</span>
										.
									</>
								) : (
									<>
										You've been invited to join{" "}
										<span className="font-semibold">
											{invitation.organizationName}
										</span>
										.
									</>
								)}
							</p>

							<div className="text-sm text-gray-500 text-center mb-6">
								This invitation expires on {formatLocalDate(invitation.expiryDate, "longDate")}
							</div>

							{error && (
								<div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
									<p className="text-sm text-red-600">{error}</p>
								</div>
							)}

							<div className="flex gap-3">
								<Button
									color="secondary"
									onClick={handleDecline}
									isLoading={declineMutation.isPending}
									isDisabled={
										acceptMutation.isPending || declineMutation.isPending
									}
									className="flex-1"
								>
									Decline
								</Button>
								<Button
									color="primary"
									onClick={handleAccept}
									isLoading={acceptMutation.isPending}
									isDisabled={
										acceptMutation.isPending || declineMutation.isPending
									}
									className="flex-1"
								>
									Accept
								</Button>
							</div>
						</>
					)}
				</div>
			</PageContentContainer>
			<ConfirmationModal
				isOpen={showDeclineModal}
				onClose={() => setShowDeclineModal(false)}
				onConfirm={handleDeclineConfirm}
				title="Decline Invitation"
				message={`Decline invitation to join ${invitation?.organizationName}? This cannot be undone.`}
				confirmText="Decline"
				cancelText="Cancel"
				isLoading={declineMutation.isPending}
			/>
		</AdminLayout>
	);
}
